import { useState, useRef, useCallback, useEffect, memo } from "react";

export interface ImageSlotProps {
  value: string | null;
  onChange: (next: string | null) => void;
  size?: { width: number; height: number };
  placeholder?: string;
  disabled?: boolean;
  label?: string;
  maxBytes?: number;
}

const DEFAULT_SIZE = { width: 140, height: 180 };
const DEFAULT_MAX_BYTES = 2097152;
const TOUCH_HOLD_MS = 500;

/**
 * ImageSlot — memoized image upload slot.
 *
 * Wrapped in React.memo: re-renders only when props change by referential equality.
 * Consumers passing function props (`onChange`) MUST stabilize them with `useCallback`
 * or extract handlers to module scope; inline arrows defeat memoization and trigger
 * re-renders on every parent render.
 */
function ImageSlotImpl({
  value,
  onChange,
  size = DEFAULT_SIZE,
  placeholder = "?",
  disabled = false,
  label,
  maxBytes = DEFAULT_MAX_BYTES,
}: ImageSlotProps) {
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const touchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const processFile = useCallback(
    (file: File) => {
      if (file.size > maxBytes) {
        setError(`Файл слишком большой (макс. ${Math.round(maxBytes / 1048576)} МБ)`);
        return;
      }
      setError(null);
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        onChange(e.target!.result as string);
      };
      reader.readAsDataURL(file);
    },
    [maxBytes, onChange],
  );

  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) processFile(file);
      e.target.value = "";
    },
    [processFile],
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) processFile(file);
    },
    [processFile],
  );

  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      onChange(null);
    },
    [onChange],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onChange(null);
    },
    [onChange],
  );

  const clearTouchTimer = useCallback(() => {
    if (touchTimerRef.current !== null) {
      clearTimeout(touchTimerRef.current);
      touchTimerRef.current = null;
    }
  }, []);

  const handleTouchStart = useCallback(
    (_e: React.TouchEvent<HTMLDivElement>) => {
      clearTouchTimer();
      touchTimerRef.current = setTimeout(() => {
        touchTimerRef.current = null;
        inputRef.current?.click();
      }, TOUCH_HOLD_MS);
    },
    [clearTouchTimer],
  );

  useEffect(() => {
    return () => {
      if (touchTimerRef.current !== null) {
        clearTimeout(touchTimerRef.current);
        touchTimerRef.current = null;
      }
    };
  }, []);

  const rootStyle: React.CSSProperties = disabled
    ? { opacity: 0.5, pointerEvents: "none", cursor: "not-allowed" }
    : {};

  return (
    <div style={rootStyle}>
      {label && (
        <div
          style={{
            font: "500 10px 'JetBrains Mono', monospace",
            letterSpacing: ".16em",
            textTransform: "uppercase",
            color: "var(--paper-ink-2)",
            marginBottom: 6,
          }}
        >
          {label}
        </div>
      )}
      <div
        style={{
          width: size.width,
          height: size.height,
          border: dragOver
            ? "2px dashed var(--paper-accent)"
            : "2px dashed var(--paper-rule)",
          background: dragOver ? "rgba(139,37,21,.05)" : "var(--paper-bg)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          position: "relative",
          overflow: "hidden",
          transition: "border-color .12s",
          flexShrink: 0,
        }}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onContextMenu={handleContextMenu}
        onTouchStart={handleTouchStart}
        onTouchEnd={clearTouchTimer}
        onTouchMove={clearTouchTimer}
        onTouchCancel={clearTouchTimer}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleInputChange}
        />
        {value !== null ? (
          <img
            src={value}
            alt=""
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          <div
            style={{
              font: "400 36px 'Fraunces', serif",
              fontStyle: "italic",
              color: "var(--paper-ink-2)",
              userSelect: "none",
              pointerEvents: "none",
            }}
          >
            {placeholder}
          </div>
        )}
        {value !== null && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Удалить изображение"
            style={{
              position: "absolute",
              top: 4,
              right: 4,
              width: 20,
              height: 20,
              border: "none",
              background: "rgba(27,23,20,.65)",
              color: "var(--paper-bg)",
              font: "500 13px sans-serif",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2,
              lineHeight: 1,
              padding: 0,
            }}
          >
            ×
          </button>
        )}
      </div>
      {error !== null && (
        <div
          style={{
            font: "500 10px 'JetBrains Mono', monospace",
            color: "var(--paper-accent)",
            marginTop: 4,
            maxWidth: size.width,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

ImageSlotImpl.displayName = "ImageSlot";

export const ImageSlot = memo(ImageSlotImpl);
